import React, { useState, useEffect } from 'react';
import {
    Drawer, Box, Typography, IconButton, List, ListItem,
    Button, Chip, CircularProgress, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab,
    Snackbar, Alert
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useNavigate } from 'react-router-dom';

import { pasesBandejaJWT, pasesAceptarJWT, pasesRechazarJWT } from '../api/endpoints';
import { capitalizar } from '../utils/capitalizar';

const useStyles = makeStyles((theme) => ({
    drawer: {
        width: 420,
        [theme.breakpoints.down('md')]: {
            width: '100%'
        }
    },
    header: {
        padding: theme.spacing(2),
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
        color: theme.palette.primary.contrastText,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    content: {
        padding: theme.spacing(1.5),
        overflowY: 'auto',
        flex: 1,
    },
    paseItem: {
        borderRadius: 4,
        marginBottom: theme.spacing(1),
        padding: theme.spacing(1.5),
        border: '1px solid #e0e0e0',
        flexDirection: 'column',
        alignItems: 'stretch',
        cursor: 'pointer',
        transition: 'background-color 0.15s',
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    },
    paseItemPendiente: {
        borderLeft: `3px solid ${theme.palette.warning.main}`,
        backgroundColor: '#fffde7',
        '&:hover': {
            backgroundColor: '#fff9c4',
        },
    },
    paseItemAceptado: {
        borderLeft: '3px solid #4caf50',
        opacity: 0.75,
        '&:hover': {
            opacity: 1,
            backgroundColor: '#f5f5f5',
        },
    },
    paseItemRechazado: {
        borderLeft: '3px solid #f44336',
        opacity: 0.75,
        '&:hover': {
            opacity: 1,
            backgroundColor: '#f5f5f5',
        },
    },
    empty: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: theme.palette.text.secondary
    }
}));

const BandejaEntradaPanel = ({ open, onClose }) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [pases, setPases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rechazarDialog, setRechazarDialog] = useState({ open: false, idPase: null });
    const [motivoRechazo, setMotivoRechazo] = useState('');
    const [procesando, setProcesando] = useState(null);
    const [tabActivo, setTabActivo] = useState(0); // 0 = pendientes, 1 = historial
    const [snackbar, setSnackbar] = useState({ open: false, mensaje: '', tipo: 'success', pase: null });

    useEffect(() => {
        if (open) { cargarBandeja(); setTabActivo(0); }
    }, [open]);

    const cargarBandeja = async () => {
        setLoading(true);
        try {
            const response = await pasesBandejaJWT();
            if (response?.ok) setPases(response.pases || []);
        } catch (err) {
            console.error('Error al cargar bandeja:', err);
        } finally {
            setLoading(false);
        }
    };

    const pendientes = pases.filter(p => p.estado === 'pendiente');
    const historial = pases.filter(p => p.estado !== 'pendiente');

    const handleAceptar = async (e, idPase) => {
        e.stopPropagation();
        const pase = pases.find(p => p.id === idPase);
        setProcesando(idPase);
        try {
            const response = await pasesAceptarJWT(idPase);
            if (response?.ok) {
                setSnackbar({ open: true, mensaje: 'Pase aceptado correctamente', tipo: 'success', pase });
                cargarBandeja();
            }
        } catch (err) {
            console.error('Error al aceptar pase:', err);
        } finally {
            setProcesando(null);
        }
    };

    const handleRechazar = async () => {
        const { idPase } = rechazarDialog;
        if (!idPase || !motivoRechazo.trim()) return;
        const pase = pases.find(p => p.id === idPase);
        setProcesando(idPase);
        try {
            const response = await pasesRechazarJWT(idPase, motivoRechazo.trim());
            if (response?.ok) {
                setRechazarDialog({ open: false, idPase: null });
                setMotivoRechazo('');
                setSnackbar({ open: true, mensaje: 'Pase rechazado y devuelto al remitente', tipo: 'warning', pase });
                cargarBandeja();
            }
        } catch (err) {
            console.error('Error al rechazar pase:', err);
        } finally {
            setProcesando(null);
        }
    };

    const getPrioridadColor = (prioridad) => {
        if (prioridad >= 3) return 'error';
        if (prioridad === 2) return 'warning';
        return 'default';
    };

    const abrirExpediente = (pase) => {
        const tipo = pase.tipoStr || 'judicial';
        navigate(`/expediente/${tipo}/${pase.idExpediente}`);
        onClose();
    };

    const renderPase = (pase) => {
        const esPendiente = pase.estado === 'pendiente';
        const esAceptado = pase.estado === 'aceptado';
        const esRechazado = pase.estado === 'rechazado';

        return (
            <ListItem
                key={pase.id}
                className={`${classes.paseItem} ${esPendiente ? classes.paseItemPendiente : esAceptado ? classes.paseItemAceptado : classes.paseItemRechazado}`}
                disableGutters
                onClick={() => abrirExpediente(pase)}
            >
                {/* Header: prioridad + fecha + estado */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        {esPendiente && pase.prioridad >= 2 && (
                            <Chip
                                icon={<PriorityHighIcon sx={{ fontSize: 14 }} />}
                                label={pase.prioridadNombre}
                                color={getPrioridadColor(pase.prioridad)}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                        )}
                        {esAceptado && (
                            <Chip
                                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                label="Aceptado"
                                color="success"
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                        )}
                        {esRechazado && (
                            <Chip
                                icon={<CancelIcon sx={{ fontSize: 14 }} />}
                                label="Rechazado"
                                color="error"
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                        )}
                        {esPendiente && (
                            <Chip
                                icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                                label="Pendiente"
                                color="warning"
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                        )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {pase.origenFecha} {pase.origenHora}
                    </Typography>
                </Box>

                {/* Título del expediente */}
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&:hover': { color: 'primary.main' },
                    }}
                >
                    {pase.caratula || pase.referencia || `Exp. #${pase.idExpediente}`}
                    <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.5 }} />
                </Typography>

                {/* Remitente */}
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <Typography variant="caption" color="text.secondary">De:</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {capitalizar(pase.origenUsuarioNombre || pase.origenUsuario)}
                    </Typography>
                </Box>

                {/* Observaciones */}
                {pase.origenObs && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mb: esPendiente ? 1 : 0 }}>
                        "{pase.origenObs}"
                    </Typography>
                )}

                {/* Respuesta (para historial) */}
                {pase.destinoObs && !esPendiente && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Respuesta: "{pase.destinoObs}"
                    </Typography>
                )}

                {/* Botones de acción solo para pendientes */}
                {esPendiente && (
                    <Box display="flex" gap={1} mt={0.5}>
                        <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={procesando === pase.id ? <CircularProgress size={14} /> : <CheckCircleIcon />}
                            onClick={(e) => handleAceptar(e, pase.id)}
                            disabled={procesando === pase.id}
                            sx={{ flex: 1, textTransform: 'none' }}
                        >
                            Aceptar
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={(e) => { e.stopPropagation(); setRechazarDialog({ open: true, idPase: pase.id }); }}
                            disabled={procesando === pase.id}
                            sx={{ flex: 1, textTransform: 'none' }}
                        >
                            Rechazar
                        </Button>
                    </Box>
                )}
            </ListItem>
        );
    };

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                classes={{ paper: classes.drawer }}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
            >
                <Box className={classes.header}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <InboxIcon />
                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                            Bandeja de Entrada
                        </Typography>
                        {pendientes.length > 0 && (
                            <Chip
                                label={pendientes.length}
                                size="small"
                                sx={{ background: '#fff', color: 'primary.main', fontWeight: 700 }}
                            />
                        )}
                    </Box>
                    <IconButton onClick={onClose} style={{ color: '#fff' }} size="large">
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Tabs
                    value={tabActivo}
                    onChange={(_, v) => setTabActivo(v)}
                    variant="fullWidth"
                    sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}
                >
                    <Tab label={`Pendientes${pendientes.length > 0 ? ` (${pendientes.length})` : ''}`} />
                    <Tab label={`Historial${historial.length > 0 ? ` (${historial.length})` : ''}`} />
                </Tabs>

                <Box className={classes.content}>
                    {loading ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : tabActivo === 0 ? (
                        pendientes.length === 0 ? (
                            <Box className={classes.empty}>
                                <InboxIcon style={{ fontSize: 64, color: '#ccc', marginBottom: 16 }} />
                                <Typography variant="body1" color="text.secondary">
                                    No tenés pases pendientes
                                </Typography>
                            </Box>
                        ) : (
                            <List disablePadding>
                                {pendientes.map(renderPase)}
                            </List>
                        )
                    ) : (
                        historial.length === 0 ? (
                            <Box className={classes.empty}>
                                <Typography variant="body1" color="text.secondary">
                                    No hay historial de pases
                                </Typography>
                            </Box>
                        ) : (
                            <List disablePadding>
                                {historial.map(renderPase)}
                            </List>
                        )
                    )}
                </Box>
            </Drawer>

            {/* Dialog de rechazo */}
            <Dialog
                open={rechazarDialog.open}
                onClose={() => { setRechazarDialog({ open: false, idPase: null }); setMotivoRechazo(''); }}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 1 } }}
            >
                <DialogTitle>Rechazar pase</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Motivo del rechazo"
                        multiline
                        rows={2}
                        value={motivoRechazo}
                        onChange={e => setMotivoRechazo(e.target.value)}
                        inputProps={{ maxLength: 100 }}
                        helperText={`${motivoRechazo.length}/100`}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setRechazarDialog({ open: false, idPase: null }); setMotivoRechazo(''); }}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleRechazar}
                        disabled={!motivoRechazo.trim() || procesando}
                    >
                        Rechazar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar de confirmación con link al expediente */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                    severity={snackbar.tipo}
                    variant="filled"
                    sx={{ width: '100%', alignItems: 'center' }}
                    action={snackbar.pase && (
                        <Button
                            color="inherit"
                            size="small"
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                            onClick={() => {
                                const p = snackbar.pase;
                                const tipo = p.tipoStr || 'judicial';
                                navigate(`/expediente/${tipo}/${p.idExpediente}`);
                                setSnackbar(s => ({ ...s, open: false }));
                                onClose();
                            }}
                        >
                            Ver expediente
                        </Button>
                    )}
                >
                    {snackbar.mensaje}
                </Alert>
            </Snackbar>
        </>
    );
};

export default BandejaEntradaPanel;
